import { TreeCategory } from '@p1/libs/catalog-service';

import { WatchlistElement } from '../watchlist-element/watchlist-element';
import { WatchlistPreviewElement } from './watchlist-preview-element';
import { WatchlistElementObjectType } from '../watchlist-element/watchlist-element-object-type.enum';
import { Watchlist } from '../watchlist/watchlist';


export const generateWatchlistPreviewElementFromElement = (
  watchlistElement: WatchlistElement,
  categoryTreeNodes?: {[uuid: string]: TreeCategory}): WatchlistPreviewElement => {

  let previewElement: WatchlistPreviewElement = {
    uuid: watchlistElement.objectUuid,
    elementUuid: watchlistElement.uuid,
    name: getPreviewNameFromWatchlistElement(watchlistElement),
    added: watchlistElement.added,
    type: watchlistElement.objectType,
    missingContent: !watchlistElement.object,
    routerLink: getRouterLinkForWatchlistPreviewElement(watchlistElement, categoryTreeNodes),
    queryParams: getQueryParamsForWatchlistPreviewElement(watchlistElement),
    imageFallback: getImageFallbackForWatchlistPreviewElement(watchlistElement),
    subtitleAbove: getSubtitleAboveForWatchlistPreviewElement(watchlistElement, categoryTreeNodes),
    subtitleBelow: getSubtitleBelowForWatchlistPreviewElement(watchlistElement),
    comment: watchlistElement.comment
  };

  previewElement = { ...previewElement, ...getImageForWatchlistPreviewElement(watchlistElement) };

  return previewElement;
};

export const generateWatchlistPreviewElementFromWatchlist = (watchlist: Watchlist): WatchlistPreviewElement => ({
  uuid: watchlist.uuid,
  name: watchlist.name,
  added: watchlist.created,
  type: 'Watchlist',
  missingContent: false,
  routerLink: '/',
  comment: watchlist.comment
});

const getPreviewNameFromWatchlistElement = (watchlistElement: WatchlistElement): string => {
  switch (watchlistElement.objectType) {
    case WatchlistElementObjectType.Product:
    case WatchlistElementObjectType.Supplier:
    case WatchlistElementObjectType.Query:
    case WatchlistElementObjectType.Comparison:
    case WatchlistElementObjectType.ProductValidation:
    case WatchlistElementObjectType.ProductSpecification:
      return watchlistElement.object?.name;
  }
};

const getRouterLinkForWatchlistPreviewElement = (watchlistElement: WatchlistElement, categoryTreeNodes?: {[uuid: string]: TreeCategory}): string[] => {
  switch (watchlistElement.objectType) {
    case WatchlistElementObjectType.Product:
      return watchlistElement.object ? ['/products', watchlistElement.object.urlPathSegment] : undefined;
    case WatchlistElementObjectType.Supplier:
      return watchlistElement.object ? ['/suppliers', watchlistElement.object.urlPathSegment] : undefined;
    case WatchlistElementObjectType.Query:
      if (categoryTreeNodes && Array.isArray(watchlistElement?.object?.basicFilters)) {
        const treeCategory = watchlistElement.object.basicFilters.find(filter => filter.id === 'tree_category');
        if (treeCategory && categoryTreeNodes.hasOwnProperty(treeCategory.value) && categoryTreeNodes[treeCategory.value].pathedSlug) {
          return ['/categories/' + categoryTreeNodes[treeCategory.value].pathedSlug];
        }
      }
      return ['/products'];
    case WatchlistElementObjectType.Comparison:
      return ['/products/comparison'];
    case WatchlistElementObjectType.ProductValidation:
      return watchlistElement.object ? ['/product-validation/result/', watchlistElement.object.uuid] : undefined;
    case WatchlistElementObjectType.ProductSpecification:
      return watchlistElement.object ? ['/productspecification/show/', watchlistElement.object.uuid] : undefined;
  }
};

const getImageFallbackForWatchlistPreviewElement = (watchlistElement: WatchlistElement): string => {
  switch (watchlistElement.objectType) {
    case WatchlistElementObjectType.Supplier:
      return 'placeholder-supplier--small.svg';
    case WatchlistElementObjectType.Query:
      return 'placeholder-productsearch--small.svg';
    case WatchlistElementObjectType.Comparison:
      return 'placeholder-comparison--small.svg';
    case WatchlistElementObjectType.Product:
    default:
      return 'placeholder--small.svg';
  }
};

const getSubtitleAboveForWatchlistPreviewElement = (watchlistElement: WatchlistElement, categoryTreeNodes?: {[uuid: string]: TreeCategory}): string => {
  switch (watchlistElement.objectType) {
    case WatchlistElementObjectType.Product:
      let categoryName = watchlistElement?.object?.category?.name;
      if (categoryTreeNodes && watchlistElement?.object?.treeCategories) {
        const matchingCategoryUuid = watchlistElement.object.treeCategories.find(uuid => categoryTreeNodes.hasOwnProperty(uuid));
        if (matchingCategoryUuid && categoryTreeNodes[matchingCategoryUuid]?.name) {
          categoryName = categoryTreeNodes[matchingCategoryUuid].name;
        }
      }
      return categoryName;
    case WatchlistElementObjectType.Query:
      return generateQuerySubtitleFromCategoryAndSupplier(watchlistElement?.object?.category, watchlistElement?.object?.supplier);
    case WatchlistElementObjectType.Comparison:
      return watchlistElement?.object?.category;
    case WatchlistElementObjectType.ProductValidation:
      return watchlistElement?.object?.category;
    case WatchlistElementObjectType.ProductSpecification:
      return watchlistElement?.object.category;
    case WatchlistElementObjectType.Supplier:
    default:
      return undefined;
  }
};

const getSubtitleBelowForWatchlistPreviewElement = (watchlistElement: WatchlistElement): string | number => {
  switch (watchlistElement.objectType) {
    case WatchlistElementObjectType.Product:
      return watchlistElement?.object?.supplier?.name;
    case WatchlistElementObjectType.Query:
      return watchlistElement?.added;
    case WatchlistElementObjectType.ProductSpecification:
      return watchlistElement?.object?.supplier;
    case WatchlistElementObjectType.Comparison:
    case WatchlistElementObjectType.Supplier:
    default:
      return undefined;
  }
};

const getImageForWatchlistPreviewElement = (watchlistElement: WatchlistElement) => {
  switch (watchlistElement.objectType) {
    case WatchlistElementObjectType.Product:
      return {
        imageUuid: watchlistElement?.object?.primaryImage?.fileId,
        imageContent: watchlistElement?.object?.primaryImage?.content,
        imageAltText: watchlistElement?.object?.primaryImage?.originalSource
      };
    case WatchlistElementObjectType.Supplier:
      return {
        imageUuid: watchlistElement?.object?.logo?.fileId,
        imageContent: watchlistElement?.object?.logo?.content,
        imageAltText: watchlistElement?.object?.logo?.originalSource
      };
    case WatchlistElementObjectType.ProductValidation:
    case WatchlistElementObjectType.ProductSpecification:
      return {
        imageUuid: watchlistElement?.object?.image?.fileId,
        imageContent: watchlistElement?.object?.image?.content,
        imageAltText: watchlistElement?.object?.image?.originalSource
      };
    case WatchlistElementObjectType.Query:
    case WatchlistElementObjectType.Comparison:
    default:
      return undefined;
  }
};

const getQueryParamsForWatchlistPreviewElement = (watchlistElement: WatchlistElement) => {
  switch (watchlistElement.objectType) {
    case WatchlistElementObjectType.Query:
      return { queryUuid: watchlistElement.objectUuid };
    case WatchlistElementObjectType.Comparison:
      return { comparisonUuid: watchlistElement.objectUuid };
    case WatchlistElementObjectType.Product:
    case WatchlistElementObjectType.Supplier:
    default:
      return undefined;
  }
};

const supplierTitleFromSupplierList = (supplier: string[]) => {
  if (Array.isArray(supplier) && supplier.length > 0) {
    if (supplier.length === 1) {
      return supplier[0];
    } else {
      return supplier.length + ' ' + $localize`:|watchlist preview suppliers name @@watchlist-preview-element.generator_suppliers-name:Suppliers`;
    }
  } else {
    return undefined;
  }
};

const generateQuerySubtitleFromCategoryAndSupplier = (category: string, supplier: string[]) => {
  if (category && Array.isArray(supplier)) {
    return category + ' - ' + supplierTitleFromSupplierList(supplier);
  } else {
    return category || supplierTitleFromSupplierList(supplier);
  }
};
